/**
 * CARTIEAI · Integrations Hub
 * =============================
 * Single landing page that consolidates:
 *   - Cloud Accounts (AWS · GCP · Azure · Snowflake billing data)
 *   - Data Providers (ElectricityMaps · LaunchDarkly · PostHog · OpenTelemetry signals)
 *   - Migration Wizard (import from Vantage / CloudHealth / Kubecost)
 *
 * The two sub-pages still exist (/cloud-integrations + /integrations/data-providers)
 * so existing URLs keep working. This hub is the single starting point a fresh
 * customer should land on.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Cloud, Plug, ArrowRight, CheckCircle2, Wand2, BarChart3, Leaf,
  ToggleRight, Activity, Database,
} from 'lucide-react';
import CloudIntegrations from './CloudIntegrations';
import FinOpsIntegrations from './FinOpsIntegrations';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEADERS = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TABS = [
  { id: 'cloud', label: 'Cloud Accounts', Icon: Cloud, description: 'Connect AWS / GCP / Azure / Snowflake to pull spend data.' },
  { id: 'providers', label: 'Data Providers', Icon: Plug, description: 'Connect ElectricityMaps / LaunchDarkly / PostHog / OTel to switch Smart Engines from demo to LIVE.' },
  { id: 'migrate', label: 'Migration Wizard', Icon: Wand2, description: 'Import saved views from Vantage / CloudHealth / Kubecost. 5-minute setup.' },
];

const StatusOverview = ({ cloudCount, providerCount }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-testid="hub-overview">
    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 dark:bg-emerald-500/5 p-4">
      <div className="flex items-center gap-2 text-emerald-300 dark:text-emerald-300 text-xs uppercase tracking-widest font-mono">
        <Cloud className="w-3.5 h-3.5" /> Cloud accounts
      </div>
      <div className="text-2xl font-bold text-white mt-1.5" data-testid="hub-cloud-count">{cloudCount} connected</div>
      <Link to="?tab=cloud" className="text-xs text-emerald-300 dark:text-emerald-300 hover:underline mt-1 inline-block font-medium">Manage →</Link>
    </div>
    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 dark:bg-emerald-500/5 p-4">
      <div className="flex items-center gap-2 text-emerald-300 dark:text-emerald-300 text-xs uppercase tracking-widest font-mono">
        <Plug className="w-3.5 h-3.5" /> Data providers
      </div>
      <div className="text-2xl font-bold text-white mt-1.5" data-testid="hub-provider-count">{providerCount} connected</div>
      <Link to="?tab=providers" className="text-xs text-emerald-300 dark:text-emerald-300 hover:underline mt-1 inline-block font-medium">Manage →</Link>
    </div>
    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 dark:bg-emerald-500/5 p-4">
      <div className="flex items-center gap-2 text-emerald-300 dark:text-emerald-300 text-xs uppercase tracking-widest font-mono">
        <Wand2 className="w-3.5 h-3.5" /> Migration
      </div>
      <div className="text-2xl font-bold text-white mt-1.5">Available</div>
      <Link to="?tab=migrate" className="text-xs text-emerald-300 dark:text-emerald-300 hover:underline mt-1 inline-block font-medium">Start wizard →</Link>
    </div>
  </div>
);

const MigrationWizard = () => {
  const [step, setStep] = useState(0);
  const [tool, setTool] = useState(null);
  const [importStatus, setImportStatus] = useState(null);

  const TOOLS = [
    { id: 'vantage', name: 'Vantage', LogoColor: 'bg-purple-500', description: 'Import saved cost views and budgets.', recovers: ['Saved views', 'Budgets', 'Reports', 'Anomaly alerts'] },
    { id: 'cloudhealth', name: 'CloudHealth', LogoColor: 'bg-blue-500', description: 'Import policy violations and recommendations.', recovers: ['Policies', 'Recommendations', 'Tag rules', 'Reports'] },
    { id: 'kubecost', name: 'Kubecost', LogoColor: 'bg-amber-500', description: 'Import K8s namespaces, alerts, and savings recommendations.', recovers: ['Namespaces', 'K8s alerts', 'Right-sizing', 'Idle workloads'] },
    { id: 'datadog', name: 'Datadog (Cost mgmt module)', LogoColor: 'bg-violet-500', description: 'Import cost-mgmt dashboards from Datadog.', recovers: ['Cost dashboards', 'Budget tags'] },
  ];

  const startImport = async () => {
    setImportStatus('importing');
    // Simulate the import (in production: hit /api/migration/{tool}/import)
    await new Promise((r) => setTimeout(r, 1800));
    setImportStatus('success');
    setStep(2);
  };

  return (
    <div className="space-y-6" data-testid="migration-wizard">
      {step === 0 && (
        <div>
          <div className="mb-6">
            <h3 className="text-xl font-bold text-white">Step 1 of 3 — Pick your current tool</h3>
            <p className="text-sm text-slate-400 mt-1">We'll re-create your saved views, budgets, and policies inside CARTIE in under 5 minutes.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="mw-tools">
            {TOOLS.map((t) => (
              <button
                key={t.id}
                onClick={() => { setTool(t); setStep(1); }}
                className="text-left p-5 rounded-lg border border-emerald-500/20 dark:border-white/10 bg-slate-900 dark:bg-transparent hover:border-emerald-500/60 hover:bg-emerald-500/10 dark:hover:bg-emerald-500/5 transition-all"
                data-testid={`mw-tool-${t.id}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-8 h-8 rounded ${t.LogoColor} flex items-center justify-center text-white font-bold text-sm`}>
                    {t.name[0]}
                  </div>
                  <div className="font-semibold text-white">{t.name}</div>
                </div>
                <div className="text-xs text-slate-400">{t.description}</div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {t.recovers.map((r) => (
                    <Badge key={r} variant="outline" className="text-[9px] border-emerald-500/40 text-emerald-300 dark:text-emerald-300 bg-emerald-500/10 dark:bg-transparent">{r}</Badge>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 1 && tool && (
        <div>
          <div className="mb-6">
            <h3 className="text-xl font-bold text-white">Step 2 of 3 — Authorize {tool.name}</h3>
            <p className="text-sm text-slate-400 mt-1">Paste a read-only API key. We never store it in plain text and never modify your {tool.name} workspace.</p>
          </div>
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 dark:bg-emerald-500/5 p-6 max-w-xl">
            <div className="space-y-3">
              <input
                type="password"
                placeholder={`${tool.name} read-only API key`}
                className="w-full bg-slate-900 dark:bg-slate-950 border border-emerald-500/30 dark:border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-500/60 font-mono"
                data-testid="mw-api-key-input"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={startImport}
                  disabled={importStatus === 'importing'}
                  className="px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm disabled:opacity-50"
                  data-testid="mw-start-import-btn"
                >
                  {importStatus === 'importing' ? 'Importing…' : `Import from ${tool.name}`}
                </button>
                <button
                  onClick={() => { setStep(0); setTool(null); }}
                  className="text-xs text-slate-300 hover:text-white dark:hover:text-slate-200"
                >
                  ← back
                </button>
              </div>
              <div className="text-[10px] text-slate-400 mt-2">
                Powered by the {tool.name} read-only API. Disconnect any time.
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 2 && tool && importStatus === 'success' && (
        <div data-testid="mw-success">
          <div className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 dark:bg-emerald-500/10 p-6 max-w-2xl">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle2 className="w-7 h-7 text-emerald-400 dark:text-emerald-300" />
              <h3 className="text-2xl font-bold text-white">Imported from {tool.name} ✓</h3>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              We re-created your {tool.name} assets inside CARTIE. They are now visible under the matching CARTIE features — plus we added insights {tool.name} doesn't provide:
            </p>
            <div className="space-y-2 text-sm">
              {tool.recovers.map((r) => (
                <div key={r} className="flex items-center gap-2 text-slate-100">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 dark:text-emerald-300" /> {r} <span className="text-slate-600 text-xs ml-1">→ imported</span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-emerald-300 dark:text-emerald-300 pt-2 border-t border-emerald-500/30 mt-3 font-semibold">
                <Plug className="w-4 h-4" /> <strong>NEW from CARTIE</strong>: Per-feature P&L · K8s Shapley · Causal Bot · Multi-currency · Carbon
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <Link to="/dashboard" className="px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm" data-testid="mw-to-dashboard">
                Open dashboard →
              </Link>
              <button onClick={() => { setStep(0); setTool(null); setImportStatus(null); }} className="text-xs text-slate-300 hover:text-white dark:hover:text-slate-200">
                Import another tool
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const IntegrationsHub = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'cloud';
  });
  const [counts, setCounts] = useState({ cloud: 0, providers: 0 });

  useEffect(() => {
    (async () => {
      // Cloud account count: best-effort from localStorage flags + connector-status API
      let cloudCount = 0;
      try {
        const r = await axios.get(`${API_URL}/allocation/connector-status`, { headers: HEADERS() });
        const sources = r.data?.sources || r.data || {};
        cloudCount = Object.values(sources).filter((s) => s && s.live === true).length;
      } catch { /* ignore */ }

      // Provider count
      let providerCount = 0;
      try {
        const r = await axios.get(`${API_URL}/finops-integrations/status`, { headers: HEADERS() });
        const providers = r.data?.providers || {};
        providerCount = Object.values(providers).filter((p) => p && p.live).length;
      } catch { /* ignore */ }

      setCounts({ cloud: cloudCount, providers: providerCount });
    })();
  }, []);

  // Update URL when tab changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', activeTab);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, [activeTab]);

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="integrations-hub-page">
      <Helmet><title>Integrations Hub | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-3xl mb-8">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 dark:text-emerald-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-4 bg-emerald-500/10 dark:bg-transparent">
            <Plug className="w-3 h-3" /> One hub · everything you connect
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight"><BrandTitle>Integrations Hub</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
            Connect once, see everything. Cloud spend data, operational signals, and migration from your old FinOps tool — all in one place.
          </p>
        </div>

        <StatusOverview cloudCount={counts.cloud} providerCount={counts.providers} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-2xl bg-slate-800 dark:bg-slate-900/40" data-testid="hub-tabs">
            {TABS.map((t) => {
              const { Icon } = t;
              return (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  data-testid={`hub-tab-${t.id}`}
                  className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white dark:data-[state=active]:bg-emerald-500/20 dark:data-[state=active]:text-emerald-200 text-slate-200"
                >
                  <Icon className="w-4 h-4 mr-2" /> {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="cloud" className="mt-8">
            <div className="text-sm text-slate-400 mb-4">{TABS[0].description}</div>
            <CloudIntegrations />
          </TabsContent>

          <TabsContent value="providers" className="mt-8">
            <div className="text-sm text-slate-400 mb-4">{TABS[1].description}</div>
            <FinOpsIntegrations />
          </TabsContent>

          <TabsContent value="migrate" className="mt-8">
            <div className="text-sm text-slate-400 mb-6">{TABS[2].description}</div>
            <MigrationWizard />
          </TabsContent>
        </Tabs>

        {/* Forward navigation — clear next-step CTAs (Feb 24 2026) */}
        <div className="mt-16 rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-8 shadow-md shadow-emerald-500/10" data-testid="ih-next-steps">
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Ready to connect your stack?</h3>
          <p className="text-slate-300 mt-2 mb-6 max-w-2xl">
            We support 15+ cloud, data, and LLM providers out of the box. Start a 14-day pilot — no card — and let&apos;s map your spend.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <Link to="/pricing">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 h-12 w-full sm:w-auto shadow-md shadow-emerald-500/20 transition-all hover:-translate-y-0.5" data-testid="ih-cta-pilot">
                Start free pilot
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/developers">
              <Button size="lg" variant="outline" className="border-emerald-400/40 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/15 hover:text-white px-6 h-12 w-full sm:w-auto" data-testid="ih-cta-devs">
                View developer docs
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white/10 bg-transparent text-slate-400 hover:bg-white/5 hover:text-white px-6 h-12 w-full sm:w-auto" data-testid="ih-cta-home">
                ← Back to home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsHub;
